import { api } from './client';

/** ч.1334: статті довідки в панелі платформи («Підтримка → Статті довідки»). */
export type ArticleKind = 'page_ref' | 'page_doc' | 'topic' | 'question';
export type DraftStatus = 'QUEUED' | 'RUNNING' | 'DONE' | 'FAILED' | 'ACCEPTED' | 'REJECTED';

export interface HelpArticleRow {
  id: string;
  slug: string;
  kind: ArticleKind;
  section: string;
  section_order: number;
  title: string;
  route_pattern: string | null;
  parent_route: string | null;
  status: 'draft' | 'published' | 'archived';
  version: number;
  edited_at: string | null;
  edited_by: string | null;
  updated_at: string;
  own: boolean;
  kb_update: boolean;
  kb_removed: boolean;
  /** ч.1421: влито в іншу статтю: клієнт не бачить, агенти читають. */
  agent_only?: boolean;
  draft_status: DraftStatus | null;
  parent_id: string | null;
  parent_title: string | null;
}

export interface ArticleText {
  title: string;
  summary: string;
  body_md: string;
  agent_note: string | null;
}

export interface KbPending extends Partial<ArticleText> {
  hash?: string;
  removed?: boolean;
}

export interface LinkRow { id: string; title: string; kind?: string | null; version?: number | null; edited_at?: string | null }
export interface RevisionRow {
  id: string; version: number; source: 'kb' | 'manual' | 'agent' | 'revert' | 'support';
  note: string | null; created_at: string; user_email: string | null;
}
export interface DraftRow {
  id: string; status: DraftStatus; instruction: string; created_at: string; finished_at: string | null;
  created_by: string | null;
}

export interface HelpArticle extends ArticleText {
  id: string;
  slug: string;
  kind: ArticleKind;
  section: string;
  route_pattern: string | null;
  parent_route: string | null;
  status: 'draft' | 'published' | 'archived';
  version: number;
  edited_at: string | null;
  edited_by: string | null;
  updated_at: string;
  own: boolean;
  source_path: string | null;
  agent_only?: boolean;
  kb_pending: KbPending | null;
  parent: LinkRow | null;
  questions: LinkRow[];
  revisions: RevisionRow[];
  drafts: DraftRow[];
  videos: { id: string; title: string; status: string }[];
}

export interface Revision extends ArticleText {
  id: string; article_id: string; version: number; source: RevisionRow['source'];
  note: string | null; created_at: string; user_email: string | null;
}

export interface AgentDraft {
  id: string;
  article_id: string;
  parent_draft_id: string | null;
  instruction: string;
  base_version: number;
  article_version: number;
  status: DraftStatus;
  declined: boolean;
  merge_kb: boolean;
  title: string | null;
  summary: string | null;
  body_md: string | null;
  agent_note: string | null;
  changes: string | null;
  doubts: string | null;
  related: { id: string; slug: string; title: string; reason: string }[];
  read_slugs: string[];
  error: string | null;
  cost_usd: number | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  created_by_email: string | null;
}

export const KIND_LABEL: Record<ArticleKind, string> = {
  page_ref: 'сторінка', page_doc: 'документ', topic: 'тема', question: 'питання-відповідь',
};

export const SOURCE_LABEL: Record<RevisionRow['source'], string> = {
  kb: 'база знань', manual: 'правка', agent: 'агент', revert: 'повернення', support: 'відповідь підтримки',
};

export const supportHelp = {
  list: (q?: string) =>
    api.get<HelpArticleRow[]>('/support-help/articles', { params: q ? { q } : {} }).then((r) => r.data),
  get: (id: string) => api.get<HelpArticle>(`/support-help/articles/${id}`).then((r) => r.data),
  edit: (id: string, expected_version: number, text: Partial<ArticleText>) =>
    api.patch<HelpArticle>(`/support-help/articles/${id}`, { expected_version, ...text }).then((r) => r.data),
  revision: (id: string) => api.get<Revision>(`/support-help/revisions/${id}`).then((r) => r.data),
  revert: (id: string, revision_id: string, expected_version: number) =>
    api.post<HelpArticle>(`/support-help/articles/${id}/revert`, { revision_id, expected_version }).then((r) => r.data),
  kb: (id: string, action: 'accept' | 'keep' | 'archive', expected_version: number) =>
    api.post<HelpArticle>(`/support-help/articles/${id}/kb`, { action, expected_version }).then((r) => r.data),
  delegate: (id: string, instruction: string, opts: { parent_draft_id?: string; merge_kb?: boolean } = {}) =>
    api.post<AgentDraft>(`/support-help/articles/${id}/drafts`, { instruction, ...opts }).then((r) => r.data),
  draft: (id: string) => api.get<AgentDraft>(`/support-help/drafts/${id}`).then((r) => r.data),
  accept: (id: string, overrides: Partial<ArticleText> = {}) =>
    api.post<HelpArticle>(`/support-help/drafts/${id}/accept`, overrides).then((r) => r.data),
  reject: (id: string) => api.post(`/support-help/drafts/${id}/reject`).then(() => undefined),
};
